import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

function VerificationCode() {
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    const inputRefs = useRef([]);
    const location = useLocation();
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const { email: primaryContactEmail, username } = location.state || {};

    useEffect(() => {
        // Redirect if no username/email in state
        if (!username || !primaryContactEmail) {
            navigate("/reset");
            return;
        }
        // Focus the first input on component mount
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [username, primaryContactEmail, navigate]);

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("Text").trim();
        if (/^\d{6}$/.test(pastedData)) {
            const digits = pastedData.split("");
            setCode(digits);
            // Focus the last input
            if (inputRefs.current[5]) {
                inputRefs.current[5].focus();
            }
        }
    };

    const handleChange = (index, value) => {
        // Only allow numbers
        if (!/^\d*$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        // Move to next input if value is entered
        if (value && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (index, e) => {
        // Move to previous input on backspace if current input is empty
        if (e.key === "Backspace" && !code[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const verificationCode = code.join("");

        try {
            const response = await axios.post("/verify-code", {
                username,
                user_code: verificationCode
            });
            if (response.data.success) {
                navigate("/password-reset", {
                    state: {
                        username,
                        primaryContactEmail
                    }
                });
                return;
            }
        } catch (err) {
            alert("Invalid verification code. Please try again.");
        }
    };

    const handleResend = async () => {
        try {
            await axios.post("/code", {
                email: primaryContactEmail,
                username
            });
            setError("");
        } catch (err) {
            setError("Error resending code. Please try again.");
        }
    };

    return (
        <div className="Homepage flex flex-col justify-center h-full">
            <div className="login-container">
                <div className="Homepage-page flex flex-col w-full max-w-md py-4 mx-auto my-auto">
                    <div className="bg-white rounded-3xl shadow p-8">
                        <h2 className="text-3xl font-arial normal-case no-underline">Enter Verification Code</h2>
                        <p className="text-gray-500 text-lg mb-8">
                            We&apos;ve sent a code to {primaryContactEmail}
                        </p>

                        <form onSubmit={handleSubmit}>
                            <div className="flex justify-center gap-3 mb-6">
                                {code.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        type="text"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        onPaste={handlePaste}
                                        className="w-14 h-14 text-2xl text-center border border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                                    />
                                ))}
                            </div>

                            {error && (
                                <div className="text-red-500 text-center mb-4">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="w-full bg-gray-100 text-black rounded-xl py-4 mb-4 font-medium hover:bg-gray-200 transition-colors"
                            >
                                Verify
                            </button>
                        </form>

                        <div className="text-center">
                            <button
                                onClick={handleResend}
                                className="text-gray-600 hover:text-gray-800 text-sm"
                            >
                                Didn&apos;t get a code? Click to resend
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VerificationCode;